import React, { useEffect, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, HStack, Icon, Progress, Text, VStack,
} from '@chakra-ui/react';
import { FaBolt, FaClock, FaPause, FaPlay, FaHandHoldingUsd, FaForward } from 'react-icons/fa';
import { MODOS, brl, valor } from './regras';
import ConfirmarAumento from './ConfirmarAumento';

const ACEITOU = ['ON_GOING', 'PICKED_UP', 'COMPLETED'];

const hora = (valor) => {
    if (!valor) return '';
    const d = new Date(valor);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const relogio = (segundos) => {
    const s = Math.max(0, Math.floor(segundos));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
};

/** Segundos ate `alvo`, atualizado a cada segundo enquanto houver alvo. */
function useContagem(alvo) {
    const [restante, setRestante] = useState(null);

    useEffect(() => {
        if (!alvo) {
            setRestante(null);
            return undefined;
        }
        const tick = () => setRestante((new Date(alvo).getTime() - Date.now()) / 1000);
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [alvo]);

    return restante;
}

/**
 * Situacao da gorjeta automatica de uma corrida ja criada.
 *
 * Responde de relance as tres perguntas do operador: quanto ja estou pagando
 * a mais, quando sobe de novo, e como paro. Parar e reversivel enquanto a
 * Lalamove procura motorista; a gorjeta ja dada nao volta (a Lalamove nao
 * deixa baixar), e a tela diz isso antes do clique.
 */
export default function PainelGorjeta({ ordem, onPausar, pausando = false, onAumentar }) {
    const g = ordem?.gorjeta;
    const restante = useContagem(g?.procurando && !g?.pausado ? g?.proximo_em : null);
    const [confirmando, setConfirmando] = useState(false);

    if (!g) return null;

    const modo = g.prioridade ? 'prioridade' : 'normal';
    const { titulo, cor } = MODOS[modo];
    const progresso = g.teto > 0 ? Math.min(100, (g.atual / g.teto) * 100) : 0;
    const noTeto = g.teto > 0 && g.atual >= g.teto - 0.004;
    const aceitou = ACEITOU.includes(ordem.status);

    // Retomar conta o tempo desde o pedido: a gorjeta pode pular varios
    // degraus de uma vez. O operador ve o valor antes de clicar.
    const segundosDesdeOPedido = (ordem.criada_em ? (Date.now() - new Date(ordem.criada_em).getTime()) / 1000 : 0)
        + (g.adiantado_seg || 0);
    const podeAumentar = !!onAumentar && g.procurando && !g.pausado && g.proximo_valor != null && !noTeto;
    const aoRetomar = g.regra ? Math.max(g.atual, valor(g.valor_original, g.regra, segundosDesdeOPedido)) : g.atual;

    let situacao;
    let corSituacao = 'gray.600';
    if (g.procurando && g.pausado) {
        situacao = `Aumentos parados${g.pausado_em ? ` às ${hora(g.pausado_em)}` : ''}. A gorjeta fica em ${brl(g.atual)}.`;
        corSituacao = 'orange.600';
    } else if (g.procurando && g.proximo_em) {
        situacao = restante !== null && restante > 0
            ? null
            : 'Aumentando a gorjeta. A tela atualiza sozinha em instantes.';
    } else if (g.procurando && noTeto) {
        situacao = 'Chegou no teto. A gorjeta não sobe mais.';
    } else if (g.procurando) {
        situacao = 'A gorjeta não sobe mais nesta corrida.';
    } else if (aceitou) {
        situacao = g.atual > 0
            ? `Motorista aceitou com ${brl(g.atual)} de gorjeta.`
            : 'Motorista aceitou sem gorjeta.';
        corSituacao = 'green.600';
    } else {
        situacao = 'Corrida encerrada.';
    }

    return (
        <Box
            borderWidth="1px"
            borderRadius="lg"
            p={{ base: 3, md: 4 }}
            borderColor={g.procurando && !g.pausado ? `${cor}.200` : 'gray.200'}
            bg={g.procurando && !g.pausado ? `${cor}.50` : 'white'}
            aria-live="polite"
        >
            <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
                <HStack spacing={2}>
                    <Icon as={FaHandHoldingUsd} color={`${cor}.500`} />
                    <Text fontWeight="bold" fontSize="sm">Gorjeta automática</Text>
                </HStack>
                <Badge colorScheme={cor} variant={g.prioridade ? 'solid' : 'subtle'} borderRadius="full" px={2}>
                    <HStack spacing={1}>
                        <Icon as={g.prioridade ? FaBolt : FaClock} boxSize={2.5} />
                        <Text as="span">{titulo}</Text>
                    </HStack>
                </Badge>
            </Flex>

            <Flex align="baseline" justify="space-between" gap={2} mb={1} wrap="wrap">
                <Text fontSize="2xl" fontWeight="bold" lineHeight="1.1" color="gray.800">{brl(g.atual)}</Text>
                <Text fontSize="xs" color="gray.500">
                    de até {brl(g.teto)}{g.teto_limitado ? ' (máx. Lalamove)' : ''}
                </Text>
            </Flex>
            <Progress
                value={progresso}
                size="sm"
                borderRadius="full"
                colorScheme={noTeto ? 'red' : cor}
                bg="whiteAlpha.700"
                aria-label={`Gorjeta em ${Math.round(progresso)}% do teto`}
                mb={2}
            />

            {g.procurando && !g.pausado && g.erro ? (
                <Text fontSize="sm" color="orange.700">
                    A Lalamove recusou a última gorjeta. O sistema tenta de novo a cada 3 minutos.
                </Text>
            ) : g.procurando && !g.pausado && g.proximo_em && restante !== null && restante > 0 ? (
                <Text fontSize="sm" color="gray.700">
                    Sobe para <b>{brl(g.proximo_valor)}</b> em{' '}
                    <Text as="span" fontWeight="bold" fontFamily="mono">{relogio(restante)}</Text>
                    {' '}se ninguém aceitar.
                </Text>
            ) : (
                <Text fontSize="sm" color={corSituacao}>{situacao}</Text>
            )}

            <Text fontSize="xs" color="gray.500" mt={1}>
                Corrida {brl(g.valor_original)} + gorjeta {brl(g.atual)} = {brl(g.valor_original + g.atual)}
            </Text>

            {g.erro && (
                <Alert status="warning" borderRadius="md" fontSize="xs" mt={3} py={2}>
                    <AlertIcon boxSize={4} />
                    <AlertDescription>{g.erro}</AlertDescription>
                </Alert>
            )}

            {g.procurando && onPausar && (
                <VStack align="stretch" spacing={1} mt={3}>
                    {g.pausado ? (
                        <Button
                            size="sm"
                            colorScheme={cor}
                            leftIcon={<Icon as={FaPlay} boxSize={3} />}
                            onClick={() => onPausar(false)}
                            isLoading={pausando}
                            loadingText="Retomando"
                            w={{ base: '100%', sm: 'auto' }}
                            alignSelf={{ sm: 'flex-start' }}
                            minH="40px"
                        >
                            Retomar aumentos
                        </Button>
                    ) : (
                        <Flex gap={2} direction={{ base: 'column', sm: 'row' }} wrap="wrap">
                            {podeAumentar && (
                                <Button
                                    size="sm"
                                    colorScheme="orange"
                                    leftIcon={<Icon as={FaForward} boxSize={3} />}
                                    onClick={() => setConfirmando(true)}
                                    minH="40px"
                                >
                                    Aumentar agora para {brl(g.proximo_valor)}
                                </Button>
                            )}
                            <Button
                                size="sm"
                                variant="outline"
                                colorScheme="gray"
                                bg="white"
                                leftIcon={<Icon as={FaPause} boxSize={3} />}
                                onClick={() => onPausar(true)}
                                isLoading={pausando}
                                loadingText="Parando"
                                isDisabled={noTeto}
                                minH="40px"
                            >
                                Parar aumentos
                            </Button>
                        </Flex>
                    )}
                    <Text fontSize="xs" color="gray.500">
                        {g.pausado
                            ? (aoRetomar > g.atual + 0.004
                                ? `Ao retomar, a gorjeta vai na hora para ${brl(aoRetomar)} (conta o tempo desde o pedido).`
                                : 'Volta a subir pelo tempo desde o pedido, até o teto.')
                            : 'A gorjeta já dada fica: a Lalamove não deixa baixar.'}
                    </Text>
                </VStack>
            )}

            {podeAumentar && (
                <ConfirmarAumento
                    isOpen={confirmando}
                    onClose={() => setConfirmando(false)}
                    ordem={ordem}
                    onConfirmar={onAumentar}
                />
            )}
        </Box>
    );
}
